<template>
  <div class="createaccount">
    <h2>Create A Customer Account</h2>
    <a ref="top" />
    <div v-if="errors" class="flex-column align-center justify-center">
      <Message>Please correct the error below</Message>
    </div>
    <PapaAutoForm
      v-model="object"
      @submit="handleSubmit"
      :errors="errors"
      :fields="fields"
    >
      <template #default>
        <div class="flex-row flex-center">
          <Button v-if="status" disabled class="grey">
            <i class="pi pi-spin pi-refresh" /> Creating Account...
          </Button>
          <Button v-else type="submit" class="grey">Create Account</Button>
        </div>
      </template>
    </PapaAutoForm>
  </div>
</template>

<route lang="json">
{ "name": "accounts-signup" }
</route>

<script setup lang="ts">
import type { FieldDef } from "@virgodev/vue-models/components/forms/defs";
import type { Ref } from "vue";

import PapaAutoForm from "@/components/PapaAutoForm.vue";
import { useAddressStore } from "@/stores/address";
import { useUserStore } from "@/stores/user";
import { useHead } from "@unhead/vue";
import api from "@virgodev/bazaar/functions/api";
import Button from "primevue/button";
import Message from "primevue/message";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const addresses = useAddressStore();
const users = useUserStore();
const status = ref<"" | "creating">();
const object: Ref<Record<string, string>> = ref({});
const errors: Ref<{ [index: string]: string[] }> = ref({});
const fields: Ref<FieldDef[]> = computed(() => {
  return [
    { name: "email", label: "Email", type: "email" },
    { name: "username", type: "username" },
    {
      label: false,
      type: "sub",
      css_classes: "hrow",
      fields: [
        { name: "first_name", label: "First Name" },
        { name: "last_name", label: "Last Name" },
      ],
    },
    ...addresses.fields,
    { name: "phone", type: "phone", label: "Phone Number" },
    { name: "password1", label: "Password", type: "password" },
    { name: "password2", label: "Confirm Password", type: "password" },
  ];
});
const top = ref<HTMLAnchorElement>();

watch(fields, () => pickupDefaults(fields.value));
watch(
  () => object.value.zip_code,
  async () => {
    try {
      await addresses.fetchCountyInfo(object.value.zip_code);
    } catch (e) {
      errors.value.zip_code = [`${e}`];
    }
  },
);

useHead({
  title: "Sign Up",
});

onMounted(() => {
  addresses.reset();
  addresses.allowPOBoxes = true;

  if (localStorage.tester) {
    const now = Date.now();
    object.value.email = `test_${now}@leithall.com`;
    object.value.username = `test_${now}`;
    object.value.first_name = "tester";
    object.value.last_name = "tester";
    object.value.phone = "4355555555";
    object.value.password1 = "Muskets for Bigfoot!";
    object.value.password2 = "Muskets for Bigfoot!";
  }
  if (object.value.zip_code) {
    addresses.fetchCountyInfo(object.value.zip_code);
  }
});

async function pickupDefaults(fields: FieldDef[]) {
  for (const field of fields) {
    if (field.default) {
      object.value[field.name] = field.default;
    } else if (field.type === "sub" && field.fields) {
      pickupDefaults(field.fields);
    }
  }
}

async function handleSubmit() {
  status.value = "creating";
  errors.value = {};
  const response = await api({
    url: "users/signup/",
    json: object.value,
    method: "POST",
  });
  if (response.ok) {
    await users.login(object.value.username, object.value.password1);
    await nextTick();
    router.push({ name: "home" });
  } else if (response.status === 400) {
    errors.value = response.body.errors;
    if (top.value) {
      top.value.scrollTo({ behavior: "smooth" });
    }
  }
  status.value = "";
}
</script>

<style scoped>
.createaccount h2 {
  margin-bottom: var(--XMd, 40px);
}

.createaccount button.grey {
  margin-top: var(--XS, 16px);
}

.auto-form :deep(.inside) {
  max-width: 738px;
  margin: 0 auto !important;
}

@media (max-width: 648px) {
  .createaccount.page {
    padding: var(--Sm, 24px);
  }
}
</style>
